// betterbase-client.js - TypeSmart BetterBase Client SDK

const BETTERBASE_CONFIG = {
  url: 'https://betterbase-api.kaywat.codes',
  apikey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InE9YjdkaSIsInJvbGUiOiJhbm9uIiwiaWF0IjoxNzg3MjAyMzEyLCJleHAiOjIxMDI1NjIzMTJ9.RYJjDFxJrKvGo3mEvF7L-V0J6nPUqBMiGw9MkG5qSTk'
};

const SESSION_KEY = 'typesmart_session';

// Environment-aware storage helper (supports extension chrome.storage and browser localStorage)
const Storage = {
  async get(key) {
    if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
      return new Promise((resolve) => {
        chrome.storage.local.get([key], (res) => resolve(res[key] || null));
      });
    }
    const val = localStorage.getItem(key);
    try { return val ? JSON.parse(val) : null; } catch (e) { return null; }
  },
  async set(key, value) {
    if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
      return new Promise((resolve) => {
        chrome.storage.local.set({ [key]: value }, () => resolve());
      });
    }
    localStorage.setItem(key, JSON.stringify(value));
  },
  async remove(key) {
    if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
      return new Promise((resolve) => {
        chrome.storage.local.remove([key], () => resolve());
      });
    }
    localStorage.removeItem(key);
  }
};

export class BetterBaseClient {
  static getHeaders(token = null) {
    const headers = {
      'Content-Type': 'application/json',
      'apikey': BETTERBASE_CONFIG.apikey,
      'User-Agent': 'TypeSmart-Web'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      headers['Authorization'] = `Bearer ${BETTERBASE_CONFIG.apikey}`;
    }
    return headers;
  }

  static async getSession() {
    return await Storage.get(SESSION_KEY);
  }

  static async saveSession(sessionData) {
    await Storage.set(SESSION_KEY, sessionData);
  }

  static async clearSession() {
    await Storage.remove(SESSION_KEY);
  }

  // --- AUTHENTICATION ---

  static async signUp(email, password) {
    const res = await fetch(`${BETTERBASE_CONFIG.url}/auth/v1/signup`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ email, password })
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error_description || data.error || data.msg || 'Signup failed');
    }

    // Auto-login after signup
    return await this.signIn(email, password);
  }

  static async signIn(email, password) {
    const res = await fetch(`${BETTERBASE_CONFIG.url}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ email, password })
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error_description || data.error || data.msg || 'Invalid email or password');
    }

    const session = {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      user: {
        id: data.user.id,
        email: data.user.email
      }
    };

    await this.saveSession(session);
    await this.ensureUserProfile(session);
    return session;
  }

  static async signInWithOAuth(provider = 'google', redirectTo = null) {
    const targetUrl = redirectTo || (typeof window !== 'undefined' ? window.location.origin + '/options.html' : '');
    const authUrl = `${BETTERBASE_CONFIG.url}/auth/v1/authorize?provider=${provider}&redirect_to=${encodeURIComponent(targetUrl)}`;
    if (typeof window !== 'undefined') {
      window.location.href = authUrl;
    }
    return authUrl;
  }

  static async signOut() {
    const session = await this.getSession();
    if (session && session.access_token) {
      try {
        await fetch(`${BETTERBASE_CONFIG.url}/auth/v1/logout`, {
          method: 'POST',
          headers: this.getHeaders(session.access_token)
        });
      } catch (e) {
        console.warn('Signout API warning:', e);
      }
    }
    await this.clearSession();
  }

  // --- USER PROFILE & USAGE LIMITS ---

  static async ensureUserProfile(session) {
    if (!session || !session.user) return null;
    const userId = session.user.id;
    const email = session.user.email;

    // Check if profile exists
    const res = await fetch(`${BETTERBASE_CONFIG.url}/rest/v1/user_profiles?user_id=eq.${userId}`, {
      headers: this.getHeaders(session.access_token)
    });

    if (res.ok) {
      const profiles = await res.json();
      if (profiles && profiles.length > 0) {
        return profiles[0];
      }
    }

    // Create default profile if missing
    const createRes = await fetch(`${BETTERBASE_CONFIG.url}/rest/v1/user_profiles`, {
      method: 'POST',
      headers: {
        ...this.getHeaders(session.access_token),
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        user_id: userId,
        email: email,
        subscription_tier: 'free',
        subscription_status: 'active',
        daily_expansions_used: 0,
        last_expansion_date: new Date().toISOString().split('T')[0]
      })
    });

    if (createRes.ok) {
      const newProfiles = await createRes.json();
      return newProfiles[0];
    }
    return null;
  }

  static async getUserProfile() {
    const session = await this.getSession();
    if (!session || !session.user) return null;

    const todayStr = new Date().toISOString().split('T')[0];
    const res = await fetch(`${BETTERBASE_CONFIG.url}/rest/v1/user_profiles?user_id=eq.${session.user.id}`, {
      headers: this.getHeaders(session.access_token)
    });

    if (res.ok) {
      const profiles = await res.json();
      if (profiles && profiles.length > 0) {
        let profile = profiles[0];
        // Reset daily usage if last expansion was on a previous date
        if (profile.last_expansion_date !== todayStr) {
          profile = await this.resetDailyUsage(profile.id, session.access_token);
        }
        return profile;
      }
    }
    return await this.ensureUserProfile(session);
  }

  static async resetDailyUsage(profileId, token) {
    const todayStr = new Date().toISOString().split('T')[0];
    const res = await fetch(`${BETTERBASE_CONFIG.url}/rest/v1/user_profiles?id=eq.${profileId}`, {
      method: 'PATCH',
      headers: {
        ...this.getHeaders(token),
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        daily_expansions_used: 0,
        last_expansion_date: todayStr
      })
    });
    if (res.ok) {
      const updated = await res.json();
      return updated[0];
    }
    return null;
  }

  static async recordExpansionUsage(shortcut = '') {
    const session = await this.getSession();
    const profile = await this.getUserProfile();

    const isFreeTier = !profile || profile.subscription_tier === 'free';
    const currentUsed = profile ? profile.daily_expansions_used : 0;
    const limit = isFreeTier ? 10 : Infinity;

    if (isFreeTier && currentUsed >= 10) {
      return {
        allowed: false,
        count: currentUsed,
        limit: 10,
        message: 'Daily free expansion limit reached (10/10). Upgrade for unlimited expansions!'
      };
    }

    const newCount = currentUsed + 1;

    if (profile && session) {
      const todayStr = new Date().toISOString().split('T')[0];
      // Update profile counter
      await fetch(`${BETTERBASE_CONFIG.url}/rest/v1/user_profiles?id=eq.${profile.id}`, {
        method: 'PATCH',
        headers: this.getHeaders(session.access_token),
        body: JSON.stringify({
          daily_expansions_used: newCount,
          last_expansion_date: todayStr
        })
      });

      // Log usage entry
      await fetch(`${BETTERBASE_CONFIG.url}/rest/v1/usage_logs`, {
        method: 'POST',
        headers: this.getHeaders(session.access_token),
        body: JSON.stringify({
          user_id: session.user.id,
          shortcut: shortcut,
          expansion_date: todayStr
        })
      });
    }

    return {
      allowed: true,
      count: newCount,
      limit: isFreeTier ? 10 : Infinity
    };
  }

  // --- CLOUD TRIGGERS SYNC ---

  static async fetchUserTriggers() {
    const session = await this.getSession();
    if (!session || !session.user) return null;

    const res = await fetch(`${BETTERBASE_CONFIG.url}/rest/v1/user_triggers?user_id=eq.${session.user.id}`, {
      headers: this.getHeaders(session.access_token)
    });

    if (res.ok) {
      const rows = await res.json();
      const triggersObj = {};
      rows.forEach(item => {
        triggersObj[item.shortcut] = {
          shortcut: item.shortcut,
          response: item.response,
          tags: Array.isArray(item.tags) ? item.tags : (item.tags ? [item.tags] : []),
          enabled: item.enabled
        };
      });
      return triggersObj;
    }
    return null;
  }

  static async saveUserTrigger(trigger) {
    const session = await this.getSession();
    if (!session || !session.user) return false;

    // Check if trigger shortcut already exists for user
    const checkRes = await fetch(`${BETTERBASE_CONFIG.url}/rest/v1/user_triggers?user_id=eq.${session.user.id}&shortcut=eq.${encodeURIComponent(trigger.shortcut)}`, {
      headers: this.getHeaders(session.access_token)
    });

    const existing = checkRes.ok ? await checkRes.json() : [];

    if (existing && existing.length > 0) {
      // Update existing
      await fetch(`${BETTERBASE_CONFIG.url}/rest/v1/user_triggers?id=eq.${existing[0].id}`, {
        method: 'PATCH',
        headers: this.getHeaders(session.access_token),
        body: JSON.stringify({
          response: trigger.response,
          tags: trigger.tags || [],
          updated_at: new Date().toISOString()
        })
      });
    } else {
      // Insert new
      await fetch(`${BETTERBASE_CONFIG.url}/rest/v1/user_triggers`, {
        method: 'POST',
        headers: this.getHeaders(session.access_token),
        body: JSON.stringify({
          user_id: session.user.id,
          shortcut: trigger.shortcut,
          response: trigger.response,
          tags: trigger.tags || []
        })
      });
    }
    return true;
  }

  static async deleteUserTrigger(shortcut) {
    const session = await this.getSession();
    if (!session || !session.user) return false;

    await fetch(`${BETTERBASE_CONFIG.url}/rest/v1/user_triggers?user_id=eq.${session.user.id}&shortcut=eq.${encodeURIComponent(shortcut)}`, {
      method: 'DELETE',
      headers: this.getHeaders(session.access_token)
    });
    return true;
  }
}

if (typeof window !== 'undefined') {
  window.BetterBaseClient = BetterBaseClient;
}

